import React, { useState, useEffect, useCallback } from 'react';
import {
  getRoyaltyClaims, approveRoyaltyClaim, rejectRoyaltyClaim,
  getRoyaltyConfig, updateRoyaltyConfig, getRoyaltyEligible, scanRoyaltyEligibility,
  distributeRoyalty, getRoyaltyDistributions,
} from '../../utils/api';
import Pagination from '../Pagination';
import CopyButton from '../CopyButton';

const TABS = ['pending', 'approved', 'rejected', 'all'];

const RoyaltyAdmin = ({ BC, fBC, vBC, vBCs } = {}) => {
  const [tab, setTab] = useState('pending');
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const [pool, setPool] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // view: 'claims' | 'eligible'
  const [view, setView] = useState('claims');
  const [elig, setElig] = useState(null);
  const [elPage, setElPage] = useState(1);
  const [elPeriod, setElPeriod] = useState('');
  const [elLoading, setElLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  // distribute flow
  const [distPreview, setDistPreview] = useState(null);
  const [showDist, setShowDist] = useState(false);
  const [distributing, setDistributing] = useState(false);

  // distribution history
  const [dists, setDists] = useState([]);
  const [distLoading, setDistLoading] = useState(false);

  // approve modal state
  const [target, setTarget] = useState(null);
  const [rewardType, setRewardType] = useState('income');
  const [rewardAmount, setRewardAmount] = useState('');
  const [rewardNote, setRewardNote] = useState('');

  // config state
  const [cfg, setCfg] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadClaims = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await getRoyaltyClaims(tab === 'all' ? '' : tab, page);
      setClaims(res.claims || []); setCounts(res.counts || {}); setPool(res.pool || 0);
      setPages(res.pagination?.pages || 1);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load claims'); }
    setLoading(false);
  }, [tab, page]);

  const loadCfg = useCallback(async () => {
    try { const res = await getRoyaltyConfig(); setCfg(res); } catch {}
  }, []);

  const loadEligible = useCallback(async () => {
    setElLoading(true);
    try {
      const res = await getRoyaltyEligible(elPeriod, elPage);
      setElig(res);
      if (!elPeriod && res.period) setElPeriod(res.period);
    } catch { setElig(null); }
    setElLoading(false);
  }, [elPeriod, elPage]);

  const doScan = async () => {
    setScanning(true); setError(''); setMsg('');
    try { const r = await scanRoyaltyEligibility(); setMsg(r.message || 'Scan complete'); loadEligible(); }
    catch (e) { setError(e.response?.data?.error || 'Scan failed'); }
    setScanning(false);
  };

  const openDistribute = async () => {
    setError(''); setMsg('');
    try { const r = await distributeRoyalty(elPeriod, false); setDistPreview(r); setShowDist(true); }
    catch (e) { setError(e.response?.data?.error || 'Preview failed'); }
  };
  const confirmDistribute = async () => {
    setDistributing(true); setError('');
    try {
      const r = await distributeRoyalty(elPeriod, true);
      setMsg(r.message || 'Rewards distributed'); setShowDist(false); setDistPreview(null); loadEligible(); loadDists();
    } catch (e) { setError(e.response?.data?.error || 'Distribute failed'); }
    setDistributing(false);
  };

  const loadDists = useCallback(async () => {
    setDistLoading(true);
    try { const r = await getRoyaltyDistributions(); setDists(r.distributions || []); } catch { setDists([]); }
    setDistLoading(false);
  }, []);

  useEffect(() => { loadClaims(); }, [loadClaims]);
  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { if (view === 'eligible') loadEligible(); }, [view, loadEligible]);
  useEffect(() => { if (view === 'distributions') loadDists(); }, [view, loadDists]);

  const openApprove = (c) => { setTarget(c); setRewardType('income'); setRewardAmount(''); setRewardNote(''); setError(''); };

  const doApprove = async () => {
    if (rewardType === 'income' && (!rewardAmount || parseFloat(rewardAmount) <= 0)) { setError('Enter a reward amount for income reward'); return; }
    setBusy(true); setError('');
    try {
      const r = await approveRoyaltyClaim(target._id, { rewardType, rewardAmount: parseFloat(rewardAmount) || 0, rewardNote });
      setMsg(r.message || 'Approved'); setTarget(null); loadClaims();
    } catch (e) { setError(e.response?.data?.error || 'Approve failed'); }
    setBusy(false);
  };

  const doReject = async (c) => {
    const reason = window.prompt(`Reject royalty claim of ${c.roboId} (${c.tierName})? Enter reason:`);
    if (!reason) return;
    setBusy(true); setError('');
    try { const r = await rejectRoyaltyClaim(c._id, reason); setMsg(r.message || 'Rejected'); loadClaims(); }
    catch (e) { setError(e.response?.data?.error || 'Reject failed'); }
    setBusy(false);
  };

  const setTier = (i, key, val) => {
    const tiers = cfg.royaltyTiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t);
    setCfg({ ...cfg, royaltyTiers: tiers });
  };

  const saveCfg = async () => {
    setSavingCfg(true); setError('');
    try {
      const r = await updateRoyaltyConfig({
        royaltyEnabled: cfg.royaltyEnabled,
        royaltyTeamScope: cfg.royaltyTeamScope,
        royaltyTiers: cfg.royaltyTiers,
      });
      setMsg('Royalty settings saved.');
      setCfg({ ...cfg, royaltyTeamScope: r.royaltyTeamScope, royaltyEnabled: r.royaltyEnabled, royaltyTiers: r.royaltyTiers });
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    setSavingCfg(false);
  };

  const badge = (s) => {
    const map = { pending: 'badge-pending', approved: 'badge-approved', rejected: 'badge-rejected' };
    return <span className={`d-status-badge ${map[s] || ''}`}>{s}</span>;
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
  const n = (v, d = 2) => (v != null ? Number(v).toFixed(d) : '0');
  // Money values are stored in USDT. Show them in the base currency (BNB) with
  // the USDT equivalent on the "Show USDT" toggle — same standard as the rest of
  // the app. mV = JSX (for display); mVs = string (for join()/template contexts).
  const mV = (usdt, d = 2) => (vBC ? vBC(usdt, d) : `${n(usdt, d)} ${BC || 'USDT'}`);
  const mVs = (usdt, d = 2) => (vBCs ? vBCs(usdt, d) : `${n(usdt, d)} ${BC || 'USDT'}`);

  return (
    <div className="fade-in" style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Royalty Income</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="mono">Pool: <strong>{mV(pool)}</strong></span>
          <button className="btn" onClick={() => setShowSettings(s => !s)}>{showSettings ? 'Hide settings' : 'Settings'}</button>
        </div>
      </div>

      {/* View switch */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button className={`btn ${view === 'claims' ? 'btn-primary' : ''}`} onClick={() => setView('claims')}>Claims</button>
        <button className={`btn ${view === 'eligible' ? 'btn-primary' : ''}`} onClick={() => setView('eligible')}>Eligible Users</button>
        <button className={`btn ${view === 'distributions' ? 'btn-primary' : ''}`} onClick={() => setView('distributions')}>Distributions</button>
      </div>

      {msg && <div className="notice notice-success" style={{ marginTop: 12 }}>{msg}</div>}
      {error && !target && <div className="notice notice-error" style={{ marginTop: 12 }}>{error}</div>}

      {/* ── Settings (dynamic tiers + scope) ── */}
      {showSettings && cfg && (
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <h3>Settings</h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={!!cfg.royaltyEnabled} onChange={e => setCfg({ ...cfg, royaltyEnabled: e.target.checked })} />
              Royalty enabled
            </label>
            <span className="text-3" style={{ fontSize: 12.5 }}>
              Qualification scope (fixed): <strong>members = direct referrals</strong> · <strong>monthly volume = whole team</strong>
            </span>
            <span className="text-3">Pool feed: {cfg.royaltyPercent}% of every buy</span>
          </div>

          <div className="table-wrap"><table className="table">
            <thead><tr><th>Level</th><th>Name</th><th>Self min (USDT)</th><th>Min members</th><th>Per-member min</th><th>Monthly team min</th></tr></thead>
            <tbody>
              {(cfg.royaltyTiers || []).map((t, i) => (
                <tr key={i}>
                  <td>L{t.level}</td>
                  <td><input className="input" value={t.name} onChange={e => setTier(i, 'name', e.target.value)} /></td>
                  <td><input className="input" type="number" value={t.selfInvestmentMin} onChange={e => setTier(i, 'selfInvestmentMin', e.target.value)} /></td>
                  <td><input className="input" type="number" value={t.minMembers} onChange={e => setTier(i, 'minMembers', e.target.value)} /></td>
                  <td><input className="input" type="number" value={t.perMemberMin} onChange={e => setTier(i, 'perMemberMin', e.target.value)} /></td>
                  <td><input className="input" type="number" value={t.monthlyTeamMin} onChange={e => setTier(i, 'monthlyTeamMin', e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <button className="btn btn-primary" onClick={saveCfg} disabled={savingCfg} style={{ marginTop: 12 }}>
            {savingCfg ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      )}

      {view === 'claims' && (<>
      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t} className={`btn ${tab === t ? 'btn-primary' : ''}`} onClick={() => { setTab(t); setPage(1); }}>
            {t[0].toUpperCase() + t.slice(1)}
            {t !== 'all' && counts[t] != null ? ` (${counts[t]})` : ''}
          </button>
        ))}
      </div>

      {/* ── Claims table ── */}
      <div className="card" style={{ marginTop: 12, padding: 0 }}>
        <div className="table-wrap"><table className="table">
          <thead><tr>
            <th>Date</th><th>User</th><th>Period</th><th>Tier</th>
            <th>Qualification</th><th>Status</th><th>Reward</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8}>Loading…</td></tr>}
            {!loading && claims.length === 0 && <tr><td colSpan={8}>No claims.</td></tr>}
            {!loading && claims.map(c => (
              <tr key={c._id}>
                <td>{fmtDate(c.requestedAt)}</td>
                <td className="mono">{c.roboId}</td>
                <td>{c.periodKey}</td>
                <td>{c.tierName} (L{c.tier})</td>
                <td className="text-3" style={{ fontSize: '.85em' }}>
                  Self {mV(c.snapshot?.selfInvestment)} · Team {mV(c.snapshot?.teamVolume)} (whole) · {c.snapshot?.qualifyingMembers} direct @≥{mV(c.snapshot?.perMemberMin, 0)}
                </td>
                <td>{badge(c.status)}</td>
                <td className="mono">
                  {c.status === 'approved'
                    ? (c.rewardType === 'income' ? mV(c.rewardAmount) : (c.rewardType === 'item' ? (c.rewardNote || 'Item') : '—'))
                    : '—'}
                </td>
                <td>
                  {c.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-primary" disabled={busy} onClick={() => openApprove(c)}>Approve</button>
                      <button className="btn btn-danger" disabled={busy} onClick={() => doReject(c)}>Reject</button>
                    </div>
                  ) : (
                    <span className="text-3">{c.processedBy ? `by ${c.processedBy}` : ''}{c.rejectionReason ? ` · ${c.rejectionReason}` : ''}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      <Pagination page={page} pages={pages} onPage={setPage} />
      </>)}

      {/* ── Eligible Users record ── */}
      {view === 'eligible' && (
        <div className="card" style={{ marginTop: 12, padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>Month:
              <select className="input" value={elPeriod} onChange={e => { setElPeriod(e.target.value); setElPage(1); }} style={{ width: 'auto' }}>
                {(!elig?.periods || elig.periods.length === 0) && <option value="">{elig?.period || '—'}</option>}
                {(elig?.periods || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <span className="text-3" style={{ fontSize: 12.5 }}>
              {elig?.counts ? `${elig.counts.eligible} eligible · ${elig.counts.claimed} claimed · ${elig.counts.unclaimed} not claimed` : ''}
              {elig?.pool != null ? ` · pool: ${mVs(elig.pool)}` : ''}
              {elig?.counts?.distributed ? ` · ${elig.counts.distributed} distributed` : ''}
              {' · members: direct · volume: directs’ team'}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={doScan} disabled={scanning} title="Recompute all users for this month and save eligible ones">{scanning ? 'Scanning…' : 'Scan now'}</button>
              <button className="btn btn-sm btn-primary" onClick={openDistribute} disabled={!elig?.counts?.eligible} title="Split the pool across levels and lock each eligible user's reward">Distribute rewards</button>
            </div>
          </div>
          <div className="table-wrap"><table className="table">
            <thead><tr>
              <th>User</th><th>Wallet</th><th>Self Investment</th><th>Team Volume</th><th>Qualified Directs</th><th>Eligible Tier</th><th>Reward</th><th>Claim</th>
            </tr></thead>
            <tbody>
              {elLoading && <tr><td colSpan={8}>Loading…</td></tr>}
              {!elLoading && (elig?.users || []).length === 0 && <tr><td colSpan={8}>No users.</td></tr>}
              {!elLoading && (elig?.users || []).map(u => (
                <tr key={u.roboId} style={u.eligible ? { background: 'var(--accent-dim)' } : undefined}>
                  <td className="mono">{u.roboId}</td>
                  <td className="mono text-3">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {u.walletAddress ? `${u.walletAddress.slice(0, 6)}...${u.walletAddress.slice(-4)}` : '-'}
                      {u.walletAddress && <CopyButton text={u.walletAddress} title="Copy wallet" size={12} />}
                    </span>
                  </td>
                  <td className="mono">{mV(u.selfInvestment)}</td>
                  <td className="mono">{mV(u.teamVolume)}</td>
                  <td className="mono">{u.qualifyingMembers}</td>
                  <td>{u.eligible ? <span className="d-status-badge badge-approved">{u.qualifiedTierName} (L{u.qualifiedTier})</span> : <span className="text-3">—</span>}</td>
                  <td className="mono">{u.distributed ? <strong className="text-accent">{mV(u.rewardAmount)}</strong> : <span className="text-3">not set</span>}</td>
                  <td>{u.claimed ? badge(u.claimStatus || 'pending') : <span className="text-3">{u.distributed ? 'ready to claim' : 'not claimed'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div style={{ padding: '0 16px 12px' }}>
            <Pagination page={elig?.pagination?.page || 1} pages={elig?.pagination?.pages || 1} total={elig?.pagination?.total} onPage={setElPage} />
          </div>
        </div>
      )}

      {/* ── Distribution history (month-wise audit) ── */}
      {view === 'distributions' && (
        <div className="card" style={{ marginTop: 12, padding: 0 }}>
          <div className="table-wrap"><table className="table">
            <thead><tr>
              <th>Month</th><th>Pool</th><th>Distributed</th><th>Carried</th><th>Recipients</th><th>Paid</th><th>By</th><th>When</th><th>Per-level</th>
            </tr></thead>
            <tbody>
              {distLoading && <tr><td colSpan={9}>Loading…</td></tr>}
              {!distLoading && dists.length === 0 && <tr><td colSpan={9}>No distributions yet.</td></tr>}
              {!distLoading && dists.map(d => (
                <tr key={d.periodKey}>
                  <td className="mono">{d.periodKey}</td>
                  <td className="mono">{mV(d.pool)}</td>
                  <td className="mono"><strong className="text-accent">{mV(d.totalDistributed)}</strong></td>
                  <td className="mono">{mV(d.carried)}</td>
                  <td className="mono">{d.recipients}</td>
                  <td className="mono">{d.claimedPaid || 0}/{d.recipients}</td>
                  <td className="text-3" style={{ fontSize: 11 }}>{d.distributedBy || '—'}{d.runs > 1 ? ` · ${d.runs}×` : ''}</td>
                  <td className="text-3" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{d.distributedAt ? new Date(d.distributedAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}</td>
                  <td className="text-3" style={{ fontSize: 11 }}>
                    {(d.levels || []).map(l => `L${l.level}: ${l.count}${l.distributed ? ` @ ${mVs(l.perUser)}` : ' (carry)'}`).join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* ── Distribute modal (preview → confirm) ── */}
      {showDist && distPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !distributing && setShowDist(false)}>
          <div className="card" style={{ width: 'min(560px, 94vw)', padding: 20 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Distribute royalty — {distPreview.period}</h3>
            <p className="text-3">Pool <strong>{mV(distPreview.pool)}</strong> split equally across {distPreview.levels.length} levels; each level’s share split equally among its eligible members. A level with no eligible members keeps its share in the pool (carried to next month).</p>
            <div className="table-wrap"><table className="table">
              <thead><tr><th>Level</th><th>Eligible</th><th>Level share</th><th>Per user</th></tr></thead>
              <tbody>
                {distPreview.levels.map(l => (
                  <tr key={l.level}>
                    <td>L{l.level} — {l.name}</td>
                    <td className="mono">{l.count}</td>
                    <td className="mono">{l.distributed ? mV(l.share) : <span className="text-3">carry</span>}</td>
                    <td className="mono">{l.distributed ? mV(l.perUser) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <p className="text-3">Distributable now: <strong>{mV(distPreview.totalDistributable)}</strong>{distPreview.carried > 0 ? <> · carried: {mV(distPreview.carried)}</> : ''}</p>
            {error && <div className="notice notice-error">{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn" disabled={distributing} onClick={() => setShowDist(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={distributing} onClick={confirmDistribute}>{distributing ? 'Distributing…' : 'Confirm & lock rewards'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve modal ── */}
      {target && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !busy && setTarget(null)}>
          <div className="card" style={{ width: 'min(440px, 92vw)', padding: 20 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Approve — {target.roboId}</h3>
            <p className="text-3">{target.tierName} (L{target.tier}) · {target.periodKey}<br />
              Self {mV(target.snapshot?.selfInvestment)} · Team {mV(target.snapshot?.teamVolume)} (whole) · {target.snapshot?.qualifyingMembers} direct members</p>

            <div className="d-field"><label>Reward type</label>
              <select className="input" value={rewardType} onChange={e => setRewardType(e.target.value)}>
                <option value="income">Income (pay USDT from pool)</option>
                <option value="item">Item / gift (no payout)</option>
                <option value="none">None (acknowledge only)</option>
              </select>
            </div>

            {rewardType === 'income' && (
              <div className="d-field"><label>Amount (USDT) — pool: {mV(pool)}</label>
                <input className="input" type="number" value={rewardAmount} onChange={e => setRewardAmount(e.target.value)} placeholder="e.g. 50" />
              </div>
            )}

            <div className="d-field"><label>Note {rewardType === 'item' ? '(item description)' : '(optional)'}</label>
              <input className="input" value={rewardNote} onChange={e => setRewardNote(e.target.value)} placeholder={rewardType === 'item' ? 'e.g. Smartwatch' : 'optional note'} />
            </div>

            {error && <div className="notice notice-error">{error}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn" disabled={busy} onClick={() => setTarget(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy} onClick={doApprove}>{busy ? 'Processing…' : 'Confirm approve'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoyaltyAdmin;